/**
 * Sync Choice Modal
 *
 * Shown when user signs in for the first time and has local data.
 * Gives them the choice to:
 * 1. Migrate local data to cloud
 * 2. Start fresh with a new cloud portfolio
 */

import { useState } from 'react';
import { Icons } from './Icons';
import { migrationService, MigrationProgress } from '../services/migrationService';
import { StorageService } from '../services/storage';
import { useConfirmation } from '../hooks/useConfirmation';
import { ConfirmationModal } from './ui/ConfirmationModal';

interface SyncChoiceModalProps {
  isOpen: boolean;
  userId: string;
  onComplete: () => void;
}

export function SyncChoiceModal({ isOpen, userId, onComplete }: SyncChoiceModalProps) {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState<MigrationProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { confirm, confirmationProps } = useConfirmation();

  if (!isOpen) return null;

  /**
   * Option 1: Migrate local data to cloud
   */
  const handleMigrateToCloud = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🚀 User chose to migrate local data to cloud...');

      const result = await migrationService.migrateToCloud(userId, (p) => {
        setProgress(p);
      });

      if (result.success) {
        console.log(`✅ Migration complete: ${result.itemsMigrated} items`);

        // Clear local portfolio data after successful migration
        // This prevents hasLocalData() from returning true on future logins
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (
            key.startsWith('portfolio_') ||
            key === 'portfolios' ||
            key === 'tokens' ||
            key === 'positions' ||
            key === 'price_alerts' ||
            key === 'active_portfolio_id'
          )) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => {
          console.log(`🗑️ Clearing migrated local data: ${key}`);
          localStorage.removeItem(key);
        });

        // Mark as done so modal doesn't show again
        StorageService.set('sync_choice_made', true);

        // Small delay to ensure storage is persisted before reload
        await new Promise(resolve => setTimeout(resolve, 500));

        // Clear service worker cache and force hard reload
        console.log('🔄 Clearing caches and forcing hard reload...');
        if ('caches' in window) {
          const cacheNames = await caches.keys();
          await Promise.all(cacheNames.map(name => caches.delete(name)));
          console.log('🗑️ Cleared browser caches:', cacheNames);
        }

        // Unregister service workers to force fresh load
        if ('serviceWorker' in navigator) {
          const registrations = await navigator.serviceWorker.getRegistrations();
          await Promise.all(registrations.map(reg => reg.unregister()));
          console.log('🗑️ Unregistered service workers:', registrations.length);
        }

        // Force reload without cache
        window.location.href = window.location.pathname + '?_reload=' + Date.now();
      } else {
        setError(`Migration échouée: ${result.errors.join(', ')}`);
      }
    } catch (err) {
      console.error('❌ Migration error:', err);
      setError(err instanceof Error ? err.message : 'Erreur de migration');
    } finally {
      setLoading(false);
      setProgress(null);
    }
  };

  /**
   * Option 2: Start fresh (delete local data, use cloud)
   */
  const handleStartFresh = async () => {
    const confirmed = await confirm({
      title: 'Démarrer à zéro',
      message: 'Vos données locales seront supprimées et vous commencerez avec un portefeuille vide. Cette action est irréversible.',
      variant: 'danger',
      confirmText: 'Supprimer et démarrer',
    });

    if (!confirmed) return;

    console.log('🗑️ User chose to start fresh...');

    // Clear all portfolio-related local data
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.startsWith('portfolio_') ||
        key === 'portfolios' ||
        key === 'tokens' ||
        key === 'positions' ||
        key === 'price_alerts'
      )) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach(key => {
      console.log(`🗑️ Removing: ${key}`);
      localStorage.removeItem(key);
    });

    // Mark as done
    StorageService.set('sync_choice_made', true);

    console.log('✅ Local data cleared, reloading...');
    window.location.reload();
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 99999,
      padding: '20px',
    }}>
      <div style={{
        background: 'var(--card-bg)',
        borderRadius: '16px',
        padding: '32px',
        maxWidth: '500px',
        width: '100%',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '24px',
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            background: 'var(--primary-bg)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Icons.Cloud size={24} style={{ color: 'var(--primary)' }} />
          </div>
          <div>
            <h2 style={{
              fontSize: '20px',
              fontWeight: 600,
              color: 'var(--text)',
              margin: 0,
            }}>
              Bienvenue !
            </h2>
            <p style={{
              fontSize: '14px',
              color: 'var(--muted)',
              margin: 0,
            }}>
              Vous avez des données locales existantes
            </p>
          </div>
        </div>

        {/* Description */}
        <p style={{
          fontSize: '15px',
          color: 'var(--text)',
          lineHeight: 1.6,
          marginBottom: '24px',
        }}>
          Nous avons détecté un portefeuille existant sur cet appareil.
          Que souhaitez-vous faire ?
        </p>

        {/* Progress indicator */}
        {progress && (
          <div style={{
            padding: '16px',
            background: 'var(--bg)',
            borderRadius: '8px',
            marginBottom: '24px',
          }}>
            <p style={{ color: 'var(--primary)', fontSize: '14px', marginBottom: '8px' }}>
              {progress.stage}
            </p>
            <div style={{
              width: '100%',
              height: '8px',
              background: 'var(--border)',
              borderRadius: '4px',
              overflow: 'hidden',
            }}>
              <div style={{
                width: `${progress.percentage}%`,
                height: '100%',
                background: 'var(--primary)',
                transition: 'width 0.3s ease',
              }} />
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{
            padding: '12px',
            background: 'var(--error-bg)',
            border: '1px solid var(--error)',
            borderRadius: '8px',
            marginBottom: '24px',
          }}>
            <p style={{ color: 'var(--error)', fontSize: '14px', margin: 0 }}>
              {error}
            </p>
          </div>
        )}

        {/* Options */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {/* Option 1: Migrate */}
          <button
            onClick={handleMigrateToCloud}
            disabled={loading}
            style={{
              width: '100%',
              padding: '16px',
              background: 'var(--primary)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '15px',
              fontWeight: 600,
              cursor: loading ? 'wait' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              opacity: loading ? 0.7 : 1,
            }}
          >
            <Icons.Upload size={20} />
            {loading ? 'Synchronisation en cours...' : 'Synchroniser mes données vers le cloud'}
          </button>

          {/* Option 2: Start fresh */}
          <button
            onClick={handleStartFresh}
            disabled={loading}
            style={{
              width: '100%',
              padding: '16px',
              background: 'transparent',
              color: 'var(--text)',
              border: '1px solid var(--border)',
              borderRadius: '12px',
              fontSize: '15px',
              fontWeight: 500,
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              opacity: loading ? 0.5 : 1,
            }}
          >
            <Icons.Trash size={20} />
            Commencer un nouveau portefeuille
          </button>
        </div>

        {/* Info */}
        <p style={{
          fontSize: '12px',
          color: 'var(--muted)',
          marginTop: '20px',
          textAlign: 'center',
          lineHeight: 1.5,
        }}>
          La synchronisation enverra vos tokens et positions vers le cloud.
          Vous pourrez ensuite y accéder depuis n'importe quel appareil.
        </p>
      </div>
      <ConfirmationModal {...confirmationProps} />
    </div>
  );
}
